import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApi, storageApiRef } from '@backstage/core-plugin-api';
import {
  BUCKET,
  VISITS_KEY,
  RecentVisit,
  pushVisit,
  removeVisit,
} from './recentSearches';

/**
 * React hook that manages the recent-visits MRU list backed by StorageApi.
 *
 * Subscribes to `storageApiRef` (bucket: `quick-search`) so the in-memory
 * state always reflects whatever is in storage — including writes from other
 * instances of this hook in the same session (e.g. if the modal is unmounted
 * and re-mounted).
 *
 * @returns visits    - Current MRU list, newest first, capped at MAX_VISITS.
 * @returns hydrated  - Starts `false`. Flips `true` on the first `observe$`
 *   emission. The modal uses this to defer rendering the "Start typing…"
 *   empty-state copy until the real value is known, preventing a one-tick
 *   flicker when `snapshot()` returns `presence: 'unknown'` on first call.
 * @returns recordVisit - Push a visit to the front, deduping by location.
 * @returns remove    - Remove a single entry by location (no-op if absent).
 * @returns clear     - Remove all entries.
 */
export function useRecentSearches() {
  // Stabilize the bucket reference so the subscribe effect only runs once.
  // useApi(storageApiRef) returns the same API singleton on every render, but
  // forBucket() constructs a new child object each call — memoising it avoids
  // spurious unsubscribe/resubscribe cycles on every re-render.
  const storageApi = useApi(storageApiRef);
  const storage = useMemo(() => storageApi.forBucket(BUCKET), [storageApi]);

  // Seed initial state from snapshot so there's no blank flash on re-open
  // when the backend can satisfy snapshot() synchronously.
  // snapshot() may return presence:'unknown' on first call with async storage
  // backends — in that case we fall back to [] and wait for observe$ to
  // deliver the real value.
  const initialSnap = storage.snapshot<RecentVisit[]>(VISITS_KEY);
  const [visits, setVisits] = useState<RecentVisit[]>(
    initialSnap.presence === 'present' ? initialSnap.value : [],
  );

  // hydrated flips true once we have a definite answer from storage.
  // WebStorageApi (localStorage) never returns presence:'unknown' — snapshot()
  // is always synchronous and definite, so we can hydrate immediately.
  // If we're running against an async storage backend that returns 'unknown',
  // we fall back to waiting for the first observe$ emission instead.
  const [hydrated, setHydrated] = useState(initialSnap.presence !== 'unknown');

  // Keep a ref to the current visits array so mutator callbacks always read
  // the latest state without closing over a potentially stale `visits` value.
  // Updated synchronously on every render (no useEffect needed — ref writes
  // don't trigger re-renders, and the assignment is always current by the
  // time any callback fires after the render).
  const visitsRef = useRef<RecentVisit[]>(visits);
  visitsRef.current = visits;

  // Subscribe to observe$ for reactive updates. Must keep the returned
  // Subscription and call unsubscribe() on cleanup — skipping this leaks
  // listeners across modal re-mounts (the modal mounts/unmounts each time
  // it opens and closes).
  useEffect(() => {
    const subscription = storage.observe$<RecentVisit[]>(VISITS_KEY).subscribe({
      next: snapshot => {
        setVisits(snapshot.presence === 'present' ? snapshot.value : []);
        // Flip exactly once — subsequent emissions don't need to touch this.
        setHydrated(true);
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [storage]);

  /**
   * Push a new visit to the front of the MRU list, deduping by location.
   *
   * Reads the latest state via `visitsRef` to avoid stale-closure bugs when
   * called multiple times in the same render cycle. Fire-and-forget — the
   * await is intentionally omitted at the call site.
   */
  const recordVisit = useCallback(
    (v: RecentVisit) => {
      const next = pushVisit(visitsRef.current, v);
      // Update the ref synchronously so that a second call before React
      // re-renders (and before observe$ delivers the new value) reads the
      // already-updated array rather than the stale one.
      visitsRef.current = next;
      storage.set(VISITS_KEY, next);
    },
    [storage],
  );

  /**
   * Remove the entry with the given location from the MRU list.
   * No-op when no entry with that location exists. Fire-and-forget.
   */
  const remove = useCallback(
    (location: string) => {
      const next = removeVisit(visitsRef.current, location);
      // Same synchronous ref update as recordVisit — prevents a rapid second
      // remove/recordVisit from reading the pre-removal array.
      visitsRef.current = next;
      storage.set(VISITS_KEY, next);
    },
    [storage],
  );

  /**
   * Clear all entries from the MRU list.
   *
   * Uses storage.remove() rather than set([]) so the stored key is fully
   * absent rather than present-with-empty-array. This keeps the initial state
   * consistent: a new user who has never visited anything gets
   * presence:'absent', not an empty array.
   */
  const clear = useCallback(() => {
    storage.remove(VISITS_KEY);
  }, [storage]);

  return { visits, hydrated, recordVisit, remove, clear };
}
